import { useEffect, useRef } from 'react';
import {
    PrequalsApi,
    PrequalificationStatus,
    RefreshPrequalResponseDto,
} from '@servicetitan/financing-api-client/src/prequals-api';
import { useDependencies } from '@servicetitan/react-ioc';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FinancingApi, LoanApplicationRead, LoanStatus2 } from '@servicetitan/financing-api-client';
import { isFinalStatus, isValidLoanId } from '@servicetitan/financing-utils-shared';

const DEFAULT_POLL_INTERVAL = 10 * 1000;
const DEFAULT_MAX_POLL_TIME = 4 * 60 * 1000;

export interface UsePrequalStatusPollingOptions {
    /** Prequalification ID to poll for status updates */
    prequalId: string | null;
    /** Loan ID to poll for status updates, only used if isHardPrequalification is true */
    loanId?: string | null;
    /** Custom poll interval in milliseconds (default: 5000) */
    pollInterval?: number;
    /** Custom max poll time in milliseconds (default: 120000) */
    maxPollTime?: number;
    /** Custom timeout callback */
    onTimeout?: (prequalId: string) => void;
    /** Custom status change callback - called when prequalification status changes */
    onStatusChange?: (
        status: PrequalificationStatus | LoanStatus2,
        prequalData: RefreshPrequalResponseDto | LoanApplicationRead,
        prequalId: string | null,
    ) => void;
    /** Whether polling is enabled (default: true when prequalId exists) */
    enabled?: boolean;
    /** Whether we're refreshing after a hard submit */
    isHardPrequalification?: boolean;
}

export interface UsePrequalStatusPollingReturn {
    /** Current prequalification data from polling */

    prequalData: RefreshPrequalResponseDto | LoanApplicationRead | undefined;
    /** Whether polling is currently active */
    isPolling: boolean;
    /** Stop polling manually */
    stopPolling: () => void;
    /** Start polling manually */
    startPolling: () => void;
    /** Reset polling state */
    reset: () => void;
}

export const usePrequalStatusPolling = (
    options: UsePrequalStatusPollingOptions,
): UsePrequalStatusPollingReturn => {
    const {
        prequalId,
        loanId,
        pollInterval = DEFAULT_POLL_INTERVAL,
        maxPollTime = DEFAULT_MAX_POLL_TIME,
        onTimeout,
        onStatusChange,
        enabled = true,
        isHardPrequalification = false,
    } = options;

    const [prequalsApi, financingApi] = useDependencies(PrequalsApi, FinancingApi);
    const queryClient = useQueryClient();
    const timeoutRef = useRef<number>();
    const previousStatusRef = useRef<PrequalificationStatus | LoanStatus2>();

    // Set up timeout when polling starts
    useEffect(() => {
        if (prequalId && enabled) {
            timeoutRef.current = setTimeout(() => {
                // Cancel the polling query
                queryClient.cancelQueries({ queryKey: ['prequalStatus', prequalId] });

                if (onTimeout) {
                    onTimeout(prequalId);
                }
                // Note: Decision data and workflow stage management should be handled in the component
            }, maxPollTime);

            return () => {
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                }
            };
        }
    }, [prequalId, enabled, queryClient, onTimeout, maxPollTime]);

    // Query for polling prequalification status
    const { data: prequalData, refetch } = useQuery<
        RefreshPrequalResponseDto | LoanApplicationRead
    >({
        queryKey: ['prequalStatus', prequalId],
        queryFn: async () => {
            // Refresh prequalification status (queries provider for updated status and returns data)
            const response = !isHardPrequalification
                ? await prequalsApi.prequalificationsRefreshPrequalification(prequalId!)
                : await financingApi.loansRefreshLoanFromProvider(loanId!);
            return response.data;
        },
        enabled: (!!prequalId || isValidLoanId(loanId)) && enabled,
        refetchInterval: query => {
            // Continue polling if we don't have data yet
            if (!query.state.data?.status) {
                return pollInterval;
            }

            // Stop polling for final statuses (Approved, Declined, Prequalified, Error, Cancelled)
            if (isFinalStatus(query.state.data.status)) {
                return false;
            }

            // Continue polling for non-final statuses
            return pollInterval;
        },
        refetchOnWindowFocus: false,
        retry: false,
    });

    // Effect to handle prequalification status changes
    useEffect(() => {
        if (prequalData?.status && (prequalId ?? loanId)) {
            // Clear timeout if we get a final status
            if (timeoutRef.current && isFinalStatus(prequalData.status)) {
                clearTimeout(timeoutRef.current);
            }

            // Call the callback if status has changed
            if (onStatusChange && previousStatusRef.current !== prequalData.status) {
                onStatusChange(prequalData.status, prequalData, prequalId);
                previousStatusRef.current = prequalData.status;
            }
        }
    }, [prequalData, prequalId, loanId, onStatusChange]);

    const stopPolling = () => {
        queryClient.cancelQueries({ queryKey: ['prequalStatus', prequalId] });
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
    };

    const startPolling = () => {
        if (prequalId) {
            refetch();
        }
    };

    const reset = () => {
        stopPolling();
        previousStatusRef.current = undefined;
    };

    return {
        prequalData,
        isPolling: !!prequalId && enabled,
        stopPolling,
        startPolling,
        reset,
    };
};
